import { type ComponentProps, type ReactNode, useState } from 'react';
import { cn } from '@/shared/lib/cn';
import { formatDate, parseDisplayDate } from '@/shared/lib/date';
import { Input } from '@/shared/ui/primitives/input';

/** Props do DatePicker: valor `YYYY-MM-DD` ou vazio. */
export type DatePickerProps = Omit<ComponentProps<typeof Input>, 'type' | 'value' | 'onChange'> & {
  value: string;
  onChange: (value: string) => void;
};

/**
 * Campo de data sempre em `dd/mm/aaaa` (independe do idioma do navegador) que entrega
 * `YYYY-MM-DD` sem conversão de fuso. Data incompleta ou inexistente é entregue como digitada,
 * para o schema rejeitar.
 */
export function DatePicker({
  value,
  onChange,
  onBlur,
  className,
  placeholder = 'dd/mm/aaaa',
  ...props
}: DatePickerProps): ReactNode {
  const [draft, setDraft] = useState<string | null>(null);
  return (
    <Input
      inputMode="numeric"
      autoComplete="off"
      maxLength={10}
      {...props}
      placeholder={placeholder}
      className={cn('tabular-nums', className)}
      value={draft ?? formatDate(value)}
      onChange={(event) => {
        const typed = maskDate(event.target.value);
        setDraft(typed);
        onChange(parseDisplayDate(typed) ?? typed);
      }}
      onBlur={(event) => {
        setDraft(null);
        onBlur?.(event);
      }}
    />
  );
}

/** Insere as barras enquanto a pessoa digita (`01012026` → `01/01/2026`). */
function maskDate(input: string): string {
  const digits = input.replace(/\D/g, '').slice(0, 8);
  return [digits.slice(0, 2), digits.slice(2, 4), digits.slice(4)]
    .filter((part) => part !== '')
    .join('/');
}
